import { BACKEND_URL } from "@/lib/backendUrl";
import { useCallback, useEffect, useState } from "react";
import { CalendarPlus, CheckCircle2, Copy, HandHeart, Link2, Loader2, Mail, Plus,
  UserPlus, XCircle } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle } from "@/components/ui/dialog";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { DateField, isoDay } from "@/components/common/DateField";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { shortDate } from "@/lib/format";

const STATUS_STYLE = {
  approved: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  submitted: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  opened: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  sent: "border-border bg-muted text-muted-foreground",
  rejected: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
  revoked: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
  published: "border-primary/30 bg-primary/10 text-primary",
  completed: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
};

const BLANK_INVITE = { name: "", email: "", role: "field_volunteer", note: "" };
const BLANK_SHIFT = {
  title: "", date: isoDay(new Date()), starts: "09:00", ends: "12:00", places: 2,
  programme_code: "", booking_ref: "", skills: "", notes: "",
};

const copy = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch { toast.error("Copy is blocked in this browser"); }
};

const CopyRow = ({ label, value, testId }) => (
  <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/20 px-3 py-2">
    <span className="flex-1 break-all font-mono text-[11px] text-muted-foreground"
          data-testid={testId}>{value}</span>
    <Button size="sm" variant="outline" className="rounded-full text-[11px]"
            data-testid={`${testId}-copy`} onClick={() => copy(value, label)}>
      <Copy className="mr-1 h-3 w-3" /> Copy
    </Button>
  </div>
);

export default function VolunteerHub() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [shifts, setShifts] = useState(null);
  const [walls, setWalls] = useState(null);
  const [roster, setRoster] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [invite, setInvite] = useState(null);
  const [inviteForm, setInviteForm] = useState(BLANK_INVITE);
  const [created, setCreated] = useState(null);
  const [review, setReview] = useState(null);
  const [reason, setReason] = useState("");
  const [shiftForm, setShiftForm] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [picked, setPicked] = useState([]);
  const [closing, setClosing] = useState(null);
  const [close, setClose] = useState({ attended: [], hours: "", note: "" });
  const [busy, setBusy] = useState("");

  const canInvite = can("volunteer", "invite");
  const canWrite = can("volunteer", "write");

  const load = useCallback(async () => {
    const [queue, rota, wall, people, progs] = await Promise.all([
      api.get("/volunteer-hub/invites"),
      api.get("/volunteer-hub/shifts"),
      api.get("/volunteer-hub/walls"),
      api.get("/volunteers"),
      api.get("/charity/programs"),
    ]);
    setData(queue.data);
    setShifts(rota.data);
    setWalls(wall.data);
    setRoster(people.data.items || []);
    setProgrammes(progs.data || []);
  }, []);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const act = async (key, fn, message) => {
    setBusy(key);
    let ok = false;
    try {
      const res = await fn();
      toast.success(typeof message === "function" ? message(res) : message);
      await load();
      ok = true;
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
    return ok;
  };

  const sendInvite = async () => {
    setBusy("invite");
    try {
      const res = await api.post("/volunteer-hub/invites", inviteForm);
      setCreated({ ...res.data, link: `${window.location.origin}${res.data.public_path}` });
      setInvite(null);
      setInviteForm(BLANK_INVITE);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const decide = async (ref, approve) => {
    const ok = await act(`decide-${ref}`, () => approve
      ? api.post(`/volunteer-hub/applications/${ref}/approve`)
      : api.post(`/volunteer-hub/applications/${ref}/reject`, { reason }),
      approve ? "Approved — their volunteer account is open" : "Application rejected");
    if (ok) { setReview(null); setReason(""); }
  };

  const publishShift = async () => {
    const ok = await act("shift", () => api.post("/volunteer-hub/shifts", {
      ...shiftForm,
      places: Number(shiftForm.places) || 1,
      skills: shiftForm.skills.split(",").map((s) => s.trim()).filter(Boolean),
    }), (res) => `${res.data.shift_ref} published`);
    if (ok) setShiftForm(null);
  };

  const saveWalls = (values) => act("walls",
    () => api.put("/volunteer-hub/walls", values), "Walls updated");

  const doAssign = async () => {
    const ok = await act("assign", () => api.post(
      `/volunteer-hub/shifts/${assigning.shift_ref}/assign`, { volunteer_ids: picked }),
      "Rota updated");
    if (ok) { setAssigning(null); setPicked([]); }
  };

  const doClose = async () => {
    const ok = await act("close", () => api.post(
      `/volunteer-hub/shifts/${closing.shift_ref}/complete`, {
        attended: close.attended,
        hours: close.hours === "" ? null : Number(close.hours),
        note: close.note,
      }), (res) => res.data.message);
    if (ok) setClosing(null);
  };

  if (!data || !shifts || !walls) return <Loading label="Loading the volunteer hub" />;

  const wallValues = walls.values;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Phase 4 · Volunteer hub"
        title="Invitations, rota and the public walls"
        description="Invite a volunteer, approve what they send back, plan the rota so that completed shifts write straight into the shared timesheet, and publish the walls of heroes and leaders on your own website."
        actions={canInvite && (
          <Button data-testid="invite-volunteer-button" onClick={() => setInvite(BLANK_INVITE)}
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            <UserPlus className="mr-1.5 h-4 w-4" /> Invite a volunteer
          </Button>
        )}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi testId="kpi-invited" label="Invitations" value={data.counters.invited} icon={Mail} />
        <Kpi testId="kpi-waiting" label="Waiting on approval" value={data.counters.waiting}
             accent="amber" icon={HandHeart} />
        <Kpi testId="kpi-approved" label="Approved" value={data.counters.approved} accent="teal" />
        <Kpi testId="kpi-shifts-upcoming" label="Upcoming shifts" value={shifts.counters.upcoming}
             icon={CalendarPlus} />
        <Kpi testId="kpi-places-open" label="Places open" value={shifts.counters.places_open}
             accent="amber" />
      </div>

      <Tabs defaultValue="invites">
        <TabsList data-testid="hub-tabs">
          <TabsTrigger value="invites" data-testid="tab-invites">Invites &amp; applications</TabsTrigger>
          <TabsTrigger value="rota" data-testid="tab-rota">Rota</TabsTrigger>
          <TabsTrigger value="walls" data-testid="tab-walls">Walls</TabsTrigger>
        </TabsList>

        <TabsContent value="invites" className="mt-4 space-y-5">
          <Panel title="Applications" kicker="What volunteers have sent back"
                 testId="panel-applications">
            <Table head={<><Th>Reference</Th><Th>Name</Th><Th>Email</Th><Th>Photo consent</Th>
              <Th>Status</Th><Th /></>} testId="applications-table">
              {data.applications.length === 0 && <Empty label="No applications yet" colSpan={6} />}
              {data.applications.map((a) => (
                <tr key={a.application_ref} className="border-b border-border/60"
                    data-testid={`application-row-${a.application_ref}`}>
                  <Td className="font-mono text-[11px]">{a.application_ref}</Td>
                  <Td>{a.name}</Td>
                  <Td className="font-mono text-[11px]">{a.email}</Td>
                  <Td>{a.consent_granted
                    ? <Chip className={STATUS_STYLE.approved}>
                        {(a.consent_scopes || []).join(", ") || "given"}
                      </Chip>
                    : <span className="text-muted-foreground">Refused</span>}</Td>
                  <Td><Chip className={STATUS_STYLE[a.status]} testId={`application-status-${a.application_ref}`}>
                    {a.status}</Chip></Td>
                  <Td>
                    <Button size="sm" variant="outline" className="rounded-full text-[11px]"
                            data-testid={`review-${a.application_ref}`}
                            onClick={() => { setReview(a); setReason(""); }}>
                      Open
                    </Button>
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>

          <Panel title="Invitations sent" kicker="Email delivery is not connected — share the link"
                 testId="panel-invites">
            <Table head={<><Th>Reference</Th><Th>Name</Th><Th>Email</Th><Th>Expires</Th>
              <Th>Status</Th><Th /></>} testId="invites-table">
              {data.invites.length === 0 && <Empty label="No invitations yet" colSpan={6} />}
              {data.invites.map((i) => (
                <tr key={i.invite_ref} className="border-b border-border/60"
                    data-testid={`invite-row-${i.invite_ref}`}>
                  <Td className="font-mono text-[11px]">{i.invite_ref}</Td>
                  <Td>{i.name}</Td>
                  <Td className="font-mono text-[11px]">{i.email}</Td>
                  <Td>{shortDate(i.expires_on)}</Td>
                  <Td><Chip className={STATUS_STYLE[i.status]}>{i.status}</Chip></Td>
                  <Td>
                    <div className="flex gap-1.5">
                      {["sent", "opened"].includes(i.status) ? (
                        <>
                          <Button size="sm" variant="outline" className="rounded-full text-[11px]"
                                  data-testid={`copy-invite-${i.invite_ref}`}
                                  onClick={() => copy(`${window.location.origin}${i.public_path}`,
                                    "Invitation link")}>
                            <Link2 className="mr-1 h-3 w-3" /> Link
                          </Button>
                          <a href={`mailto:${i.email}?subject=${encodeURIComponent("Volunteering with us")}`
                            + `&body=${encodeURIComponent(`Please complete your volunteer registration here: ${window.location.origin}${i.public_path}`)}`}
                             data-testid={`mail-invite-${i.invite_ref}`}
                             className="inline-flex items-center rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground transition-colors duration-150 hover:text-foreground">
                            <Mail className="mr-1 h-3 w-3" /> Email
                          </a>
                          <a href={`https://wa.me/?text=${encodeURIComponent(`Please complete your volunteer registration: ${window.location.origin}${i.public_path}`)}`}
                             target="_blank" rel="noreferrer"
                             data-testid={`whatsapp-invite-${i.invite_ref}`}
                             className="inline-flex items-center rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground transition-colors duration-150 hover:text-foreground">
                            WhatsApp
                          </a>
                          {canInvite && (
                            <Button size="sm" variant="outline"
                                    className="rounded-full text-[11px] text-red-600 dark:text-red-400"
                                    data-testid={`revoke-invite-${i.invite_ref}`}
                                    disabled={busy === `revoke-${i.invite_ref}`}
                                    onClick={() => act(`revoke-${i.invite_ref}`,
                                      () => api.post(`/volunteer-hub/invites/${i.invite_ref}/revoke`),
                                      `${i.invite_ref} withdrawn — the link no longer works`)}>
                              Withdraw
                            </Button>
                          )}
                        </>
                      ) : (
                        <span className="text-[11.5px] text-muted-foreground">—</span>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="rota" className="mt-4 space-y-5">
          <Panel title="Shifts" kicker="Completing a shift writes a timesheet entry for everyone who came"
                 testId="panel-shifts"
                 actions={canWrite && (
                   <Button size="sm" data-testid="publish-shift-button"
                           onClick={() => setShiftForm(BLANK_SHIFT)}
                           className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                     <Plus className="mr-1.5 h-3.5 w-3.5" /> Publish a shift
                   </Button>
                 )}>
            <Table head={<><Th>Reference</Th><Th>Shift</Th><Th>When</Th><Th>Places</Th>
              <Th>Taken by</Th><Th>Status</Th><Th /></>} testId="shifts-table">
              {shifts.items.length === 0 && <Empty label="No shifts published" colSpan={7} />}
              {shifts.items.map((s) => (
                <tr key={s.shift_ref} className="border-b border-border/60"
                    data-testid={`shift-row-${s.shift_ref}`}>
                  <Td className="font-mono text-[11px]">{s.shift_ref}</Td>
                  <Td>{s.title}</Td>
                  <Td className="whitespace-nowrap">{shortDate(s.date)} · {s.starts}–{s.ends}</Td>
                  <Td className="font-mono text-[11px]">
                    {(s.assigned || []).length}/{s.places}
                  </Td>
                  <Td className="text-[11.5px] text-muted-foreground">
                    {(s.assigned || []).map((a) => a.name).join(", ") || "—"}
                  </Td>
                  <Td><Chip className={STATUS_STYLE[s.status]}>{s.status}</Chip></Td>
                  <Td>
                    {canWrite && s.status !== "completed" && (
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" className="rounded-full text-[11px]"
                                data-testid={`assign-${s.shift_ref}`}
                                onClick={() => { setAssigning(s); setPicked([]); }}>
                          Assign
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full text-[11px]"
                                data-testid={`complete-${s.shift_ref}`}
                                onClick={() => {
                                  setClosing(s);
                                  setClose({
                                    attended: (s.assigned || []).map((a) => a.volunteer_id),
                                    hours: String(s.hours), note: "",
                                  });
                                }}>
                          Complete
                        </Button>
                      </div>
                    )}
                    {s.status === "completed" && (
                      <span className="font-mono text-[10.5px] text-muted-foreground">
                        {(s.time_entries || []).join(", ") || "—"}
                      </span>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="walls" className="mt-4 space-y-5">
          {[["heroes", "Wall of Community Heroes",
             "Volunteers who gave permission for their name and story to be used on your website."],
            ["leaders", "Wall of Leaders",
             "Your trustees and office bearers, in order of office."]].map(([key, title, note]) => (
            <Panel key={key} title={title} kicker={note} testId={`panel-wall-${key}`}
                   actions={canWrite && (
                     <div className="flex items-center gap-2">
                       <span className="text-[11.5px] text-muted-foreground">Published</span>
                       <Switch checked={Boolean(wallValues[`${key}_enabled`])}
                               data-testid={`wall-switch-${key}`}
                               onCheckedChange={(v) => saveWalls({
                                 ...wallValues, [`${key}_enabled`]: v,
                               })} />
                     </div>
                   )}>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-[11.5px] text-muted-foreground">Introduction</Label>
                  <Textarea rows={2} value={wallValues[`${key}_intro`] || ""}
                            data-testid={`wall-intro-${key}`} disabled={!canWrite}
                            onChange={(e) => setWalls({
                              ...walls,
                              values: { ...wallValues, [`${key}_intro`]: e.target.value },
                            })} />
                  {canWrite && (
                    <Button size="sm" variant="outline" className="rounded-full text-[11px]"
                            data-testid={`wall-save-${key}`} disabled={busy === "walls"}
                            onClick={() => saveWalls(wallValues)}>
                      Save wording
                    </Button>
                  )}
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="space-y-2">
                    <p className="text-[11px] uppercase tracking-[1.4px] text-muted-foreground">
                      Put it on your own website
                    </p>
                    <CopyRow label="Public link" testId={`wall-link-${key}`}
                             value={walls.links[key]} />
                    <CopyRow label="Embed snippet" testId={`wall-embed-${key}`}
                             value={walls.embeds[key]} />
                    <img alt={`${title} QR code`} data-testid={`wall-qr-${key}`}
                         className="h-28 w-28 rounded-xl border border-border bg-white p-1.5"
                         src={`${BACKEND_URL}/api/public/give/qr.png?url=${encodeURIComponent(walls.links[key])}`} />
                  </div>
                  <div className="space-y-2">
                    <p className="text-[11px] uppercase tracking-[1.4px] text-muted-foreground">
                      Who appears ({walls[key].length})
                    </p>
                    <ul className="max-h-56 space-y-1.5 overflow-auto pr-1"
                        data-testid={`wall-people-${key}`}>
                      {walls[key].length === 0 && (
                        <li className="text-[12px] text-muted-foreground">
                          Nobody qualifies yet.
                        </li>
                      )}
                      {walls[key].map((p, i) => (
                        <li key={i}
                            className="flex items-center justify-between rounded-xl border border-border px-3 py-1.5">
                          <span className="text-[12.5px] text-foreground">{p.name}</span>
                          <span className="font-mono text-[10.5px] capitalize text-muted-foreground">
                            {key === "heroes" ? `${p.hours}h` : p.office}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </Panel>
          ))}
        </TabsContent>
      </Tabs>

      {/* invite */}
      <Dialog open={Boolean(invite)} onOpenChange={(o) => !o && setInvite(null)}>
        <DialogContent data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite a volunteer</DialogTitle>
            <DialogDescription>
              They complete their own details, choose a password and state their photo consent. You
              approve it before their account opens.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[11.5px] text-muted-foreground">Full name</Label>
              <Input value={inviteForm.name} data-testid="invite-name"
                     onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11.5px] text-muted-foreground">Email address</Label>
              <Input value={inviteForm.email} data-testid="invite-email"
                     onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11.5px] text-muted-foreground">Note (for your own record)</Label>
              <Input value={inviteForm.note} data-testid="invite-note"
                     onChange={(e) => setInviteForm({ ...inviteForm, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={sendInvite} data-testid="invite-submit"
                    disabled={busy === "invite" || inviteForm.name.trim().length < 2
                      || !inviteForm.email.includes("@")}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy === "invite" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create the invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* invitation link */}
      <Dialog open={Boolean(created)} onOpenChange={(o) => !o && setCreated(null)}>
        <DialogContent data-testid="invite-link-dialog">
          <DialogHeader>
            <DialogTitle>{created?.invite_ref} is ready</DialogTitle>
            <DialogDescription>
              Email delivery is not connected for this charity yet, so send the link yourself. It
              expires on {created ? shortDate(created.expires_on) : ""}.
            </DialogDescription>
          </DialogHeader>
          {created && <CopyRow label="Invitation link" testId="created-invite-link"
                               value={created.link} />}
        </DialogContent>
      </Dialog>

      {/* application review */}
      <Dialog open={Boolean(review)} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent className="max-w-lg" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle>{review?.name}</DialogTitle>
            <DialogDescription>{review?.application_ref} · {review?.email}</DialogDescription>
          </DialogHeader>
          {review && (
            <div className="max-h-[52vh] space-y-2.5 overflow-auto pr-1">
              {review.photo_path && (
                <img alt="" data-testid="review-photo"
                     src={`${BACKEND_URL}/api/volunteer-hub/applications/${review.application_ref}/photo`}
                     className="h-24 w-24 rounded-xl object-cover" />
              )}
              {[["Telephone", review.phone], ["Date of birth", review.date_of_birth],
                ["Address", review.address], ["Occupation", review.occupation],
                ["Education", review.education], ["Family background", review.family_background],
                ["Skills", (review.skills || []).join(", ")],
                ["Languages", (review.languages || []).join(", ")],
                ["Availability", review.availability], ["Introduction", review.bio],
                ["Emergency contact", [review.emergency_contact_name,
                  review.emergency_contact_phone].filter(Boolean).join(" · ")],
                ["Photo and story consent", review.consent_granted
                  ? `Given for ${(review.consent_scopes || []).join(", ")}` : "Refused"],
              ].map(([label, value]) => (
                <div key={label} className="grid grid-cols-[150px_1fr] gap-2">
                  <span className="text-[11.5px] text-muted-foreground">{label}</span>
                  <span className="text-[12.5px] text-foreground">{value || "—"}</span>
                </div>
              ))}
              {review.status === "submitted" && canInvite && (
                <div className="space-y-1.5 pt-2">
                  <Label className="text-[11.5px] text-muted-foreground">
                    Reason, if you are not accepting this (10 characters or more)
                  </Label>
                  <Textarea rows={2} value={reason} data-testid="review-reason"
                            onChange={(e) => setReason(e.target.value)} />
                </div>
              )}
            </div>
          )}
          {review?.status === "submitted" && canInvite && (
            <DialogFooter>
              <Button variant="outline" data-testid="review-reject"
                      disabled={busy.startsWith("decide") || reason.trim().length < 10}
                      onClick={() => decide(review.application_ref, false)}
                      className="rounded-full text-[12px]">
                <XCircle className="mr-1.5 h-3.5 w-3.5" /> Reject
              </Button>
              <Button data-testid="review-approve" disabled={busy.startsWith("decide")}
                      onClick={() => decide(review.application_ref, true)}
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Approve and open their account
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* publish a shift */}
      <Dialog open={Boolean(shiftForm)} onOpenChange={(o) => !o && setShiftForm(null)}>
        <DialogContent data-testid="shift-dialog">
          <DialogHeader>
            <DialogTitle>Publish a shift</DialogTitle>
            <DialogDescription>
              Volunteers see open shifts in their own account and take a place themselves.
            </DialogDescription>
          </DialogHeader>
          {shiftForm && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-[11.5px] text-muted-foreground">What is the shift</Label>
                <Input value={shiftForm.title} data-testid="shift-title"
                       onChange={(e) => setShiftForm({ ...shiftForm, title: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[11.5px] text-muted-foreground">Date</Label>
                  <DateField value={shiftForm.date} testId="shift-date" clearable={false}
                             onChange={(v) => setShiftForm({ ...shiftForm, date: v })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11.5px] text-muted-foreground">Places</Label>
                  <Input type="number" min="1" value={shiftForm.places} data-testid="shift-places"
                         onChange={(e) => setShiftForm({ ...shiftForm, places: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11.5px] text-muted-foreground">Starts</Label>
                  <Input type="time" value={shiftForm.starts} data-testid="shift-starts"
                         onChange={(e) => setShiftForm({ ...shiftForm, starts: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11.5px] text-muted-foreground">Ends</Label>
                  <Input type="time" value={shiftForm.ends} data-testid="shift-ends"
                         onChange={(e) => setShiftForm({ ...shiftForm, ends: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11.5px] text-muted-foreground">
                  Against a programme (optional)
                </Label>
                <Select value={shiftForm.programme_code || "none"}
                        onValueChange={(v) => setShiftForm({
                          ...shiftForm, programme_code: v === "none" ? "" : v,
                        })}>
                  <SelectTrigger data-testid="shift-programme"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">General help</SelectItem>
                    {programmes.map((p) => (
                      <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11.5px] text-muted-foreground">
                  Skills wanted (comma separated)
                </Label>
                <Input value={shiftForm.skills} data-testid="shift-skills"
                       onChange={(e) => setShiftForm({ ...shiftForm, skills: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={publishShift} data-testid="shift-submit"
                    disabled={busy === "shift" || (shiftForm?.title || "").trim().length < 3}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy === "shift" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Publish the shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* assign */}
      <Dialog open={Boolean(assigning)} onOpenChange={(o) => !o && setAssigning(null)}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle>Put volunteers on {assigning?.shift_ref}</DialogTitle>
            <DialogDescription>
              {assigning?.title} · {assigning && shortDate(assigning.date)} ·{" "}
              {assigning ? Math.max(assigning.places - (assigning.assigned || []).length, 0) : 0}{" "}
              places free
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[46vh] space-y-1.5 overflow-auto pr-1" data-testid="assign-list">
            {roster.map((v) => {
              const already = (assigning?.assigned || []).some((a) => a.volunteer_id === v.id);
              return (
                <label key={v.id}
                       className="flex items-center gap-2.5 rounded-xl border border-border px-3 py-2">
                  <Checkbox disabled={already} checked={already || picked.includes(v.id)}
                            data-testid={`assign-pick-${v.id}`}
                            onCheckedChange={() => setPicked(picked.includes(v.id)
                              ? picked.filter((p) => p !== v.id) : [...picked, v.id])} />
                  <span className="flex-1 text-[12.5px] text-foreground">{v.name}</span>
                  <span className="font-mono text-[10.5px] text-muted-foreground">
                    {already ? "on the shift" : v.clearance_state}
                  </span>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button onClick={doAssign} data-testid="assign-submit"
                    disabled={busy === "assign" || picked.length === 0}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy === "assign" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add to the shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* complete */}
      <Dialog open={Boolean(closing)} onOpenChange={(o) => !o && setClosing(null)}>
        <DialogContent data-testid="complete-dialog">
          <DialogHeader>
            <DialogTitle>Close {closing?.shift_ref}</DialogTitle>
            <DialogDescription>
              Tick everyone who came. A timesheet entry is written for each of them and waits on your
              approval in Time &amp; Hours.
            </DialogDescription>
          </DialogHeader>
          {closing && (
            <div className="space-y-3">
              <div className="space-y-1.5" data-testid="attendance-list">
                {(closing.assigned || []).length === 0 && (
                  <p className="text-[12px] text-muted-foreground">
                    Nobody is on this shift — assign someone first.
                  </p>
                )}
                {(closing.assigned || []).map((a) => (
                  <label key={a.volunteer_id}
                         className="flex items-center gap-2.5 rounded-xl border border-border px-3 py-2">
                    <Checkbox checked={close.attended.includes(a.volunteer_id)}
                              data-testid={`attended-${a.volunteer_id}`}
                              onCheckedChange={() => setClose({
                                ...close,
                                attended: close.attended.includes(a.volunteer_id)
                                  ? close.attended.filter((x) => x !== a.volunteer_id)
                                  : [...close.attended, a.volunteer_id],
                              })} />
                    <span className="text-[12.5px] text-foreground">{a.name}</span>
                  </label>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[11.5px] text-muted-foreground">Hours each</Label>
                  <Input type="number" step="0.25" value={close.hours} data-testid="complete-hours"
                         onChange={(e) => setClose({ ...close, hours: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11.5px] text-muted-foreground">Note</Label>
                  <Input value={close.note} data-testid="complete-note"
                         onChange={(e) => setClose({ ...close, note: e.target.value })} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={doClose} data-testid="complete-submit"
                    disabled={busy === "close" || close.attended.length === 0}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy === "close" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Close the shift and record the hours
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
